import { WebPlugin } from '@capacitor/core';
/**
 * Web implementation of AudioInput plugin
 * Uses Web Audio API for browser-based audio capture
 */
export class AudioInputWeb extends WebPlugin {
    constructor() {
        super(...arguments);
        this.audioContext = null;
        this.mediaStream = null;
        this.scriptProcessor = null;
        this.micGainNode = null;
        this.capturing = false;
        this.options = {};
    }
    async initialize(options) {
        this.options = Object.assign(Object.assign({}, this.options), options);
        return Promise.resolve();
    }
    async checkMicrophonePermission() {
        // In web, we can only check after requesting
        // Return true if we already have a stream
        return { granted: this.mediaStream !== null };
    }
    async getMicrophonePermission() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            // We got permission, but we'll close this stream for now
            // It will be reopened in start()
            stream.getTracks().forEach(track => track.stop());
            return { granted: true };
        }
        catch (error) {
            console.error('Microphone permission denied:', error);
            return { granted: false };
        }
    }
    async start(options) {
        if (this.capturing) {
            throw new Error('Already capturing audio');
        }
        this.options = Object.assign(Object.assign({}, this.options), options);
        try {
            // Request microphone access
            this.mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: false,
                    autoGainControl: false,
                    noiseSuppression: false,
                },
            });
            // Create audio context
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            this.audioContext = new AudioContext();
            // Create nodes
            const source = this.audioContext.createMediaStreamSource(this.mediaStream);
            this.micGainNode = this.audioContext.createGain();
            // Create script processor for audio data
            const bufferSize = this.options.bufferSize || 16384;
            this.scriptProcessor = this.audioContext.createScriptProcessor(bufferSize, 1, 1);
            this.scriptProcessor.onaudioprocess = (event) => {
                if (!this.capturing)
                    return;
                const inputData = event.inputBuffer.getChannelData(0);
                const samples = this.processSamples(inputData);
                this.notifyListeners('audioData', { data: samples });
            };
            // Connect the audio graph
            source.connect(this.micGainNode);
            this.micGainNode.connect(this.scriptProcessor);
            this.scriptProcessor.connect(this.audioContext.destination);
            this.capturing = true;
        }
        catch (error) {
            this.notifyListeners('audioError', {
                message: error.message || 'Failed to start audio capture',
            });
            throw error;
        }
    }
    async stop() {
        this.capturing = false;
        if (this.scriptProcessor) {
            this.scriptProcessor.disconnect();
            this.scriptProcessor = null;
        }
        if (this.micGainNode) {
            this.micGainNode.disconnect();
            this.micGainNode = null;
        }
        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach(track => track.stop());
            this.mediaStream = null;
        }
        if (this.audioContext) {
            await this.audioContext.close();
            this.audioContext = null;
        }
        return {};
    }
    /**
     * Process audio samples according to options
     */
    processSamples(inputData) {
        const normalize = this.options.normalize !== false;
        const normalizationFactor = this.options.normalizationFactor || 32767.0;
        if (normalize) {
            // Return as Float32Array (already normalized -1 to 1)
            return Array.from(inputData);
        }
        else {
            // Convert to Int16Array
            const output = new Array(inputData.length);
            for (let i = 0; i < inputData.length; i++) {
                const sample = Math.max(-1, Math.min(1, inputData[i]));
                output[i] = Math.floor(sample * normalizationFactor);
            }
            return output;
        }
    }
}
